/* ═══════════════════════════════════════════════════════════════
   ALTERLY — HSV / HSL Adjustment Node
   Precise hue shifting, saturation tuning, and value / luminance control.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER, GLSL_COLOR_UTILS } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const HSV_SHADER = `${GLSL_HEADER}
${GLSL_COLOR_UTILS}
uniform sampler2D u_image;
uniform float u_hueShift; // -0.5 .. 0.5
uniform float u_satScale; // 0.0 .. 2.0
uniform float u_valScale; // 0.0 .. 2.0

void main() {
  vec4 src = texture(u_image, v_uv);
  vec3 hsv = rgb2hsv(src.rgb);
  
  // Shift hue and wrap around [0, 1]
  hsv.x = fract(hsv.x + u_hueShift + 1.0);
  
  // Scale saturation and value
  hsv.y = clamp(hsv.y * u_satScale, 0.0, 1.0);
  hsv.z = clamp(hsv.z * u_valScale, 0.0, 1.0);
  
  vec3 rgb = hsv2rgb(hsv);
  fragColor = vec4(rgb, src.a);
}
`;

export const hsvNode: NodeDefinition = {
  type: 'color.hsv',
  category: 'Color',
  label: 'HSV Adjust',
  description: 'Fine-tunes color hue shift, saturation, and lightness / brightness values',
  keywords: ['hsv', 'hsl', 'hue', 'saturation', 'value', 'color', 'shift', 'tint'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Output', type: 'image' },
  ],
  params: [
    {
      id: 'hue',
      name: 'Hue Shift',
      type: 'slider',
      defaultValue: 0,
      min: -180,
      max: 180,
      step: 1,
      unit: '°',
      uiHint: 'angle',
    },
    {
      id: 'saturation',
      name: 'Saturation',
      type: 'slider',
      defaultValue: 0,
      min: -100,
      max: 100,
      step: 1,
      unit: '%',
    },
    {
      id: 'value',
      name: 'Value (Lightness)',
      type: 'slider',
      defaultValue: 0,
      min: -100,
      max: 100,
      step: 1,
      unit: '%',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: HSV_SHADER,
    getUniforms: (inputs, params) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_hueShift: (Number(params.hue) || 0) / 360,
      u_satScale: 1.0 + (Number(params.saturation) || 0) / 100,
      u_valScale: 1.0 + (Number(params.value) || 0) / 100,
    }),
  }),
};
